import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import { IReportsRepository } from "../../../domain/repositories/reports-repository";
import { ReportsFilterContext } from "./ReportsFilterProvider";
import { TPageSpeed } from "domain/entities/page-speed-response";
import { createBlobDownloader } from "utils/files/downloadBlob";
import { checkParamsCreativeOnly } from "helper/filtersHelper";

type TPageSpeedProviderProps = {
  requestParamsIndex: number;
  children: React.ReactNode;
  reportsDatasource: IReportsRepository;
};

export type TPageSpeedState = {
  requestParamsIndex: number;
  pageSpeedData?: TPageSpeed;
  downloadingReports?: boolean;
  downloadPageSpeedReport: () => void;
  isLoading: boolean;
};

const initContext: TPageSpeedState = {
  requestParamsIndex: 0,
  downloadPageSpeedReport: () => null,
  isLoading: true,
};
export const PageSpeedContext = createContext(initContext);

export const PageSpeedProvider = ({
  requestParamsIndex,
  children,
  reportsDatasource,
}: TPageSpeedProviderProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [pageSpeedData, setPageSpeedData] = useState<TPageSpeed>();
  const [downloadingReports, setDownloadingReports] = useState(false);
  const { requestParams, comparisonEnabled } = useContext(ReportsFilterContext);
  const currentRequestParams = requestParams[requestParamsIndex];

  const downloadPageSpeedReport = useCallback(() => {
    setDownloadingReports(true);
    return reportsDatasource
      .exportPageSpeedCSV(currentRequestParams)
      .then(createBlobDownloader("page-speed.zip"))
      .finally(() => setDownloadingReports(false));
  }, [currentRequestParams]);

  const canShowPanel = useMemo(() => {
    if (comparisonEnabled) {
      return requestParams.every(checkParamsCreativeOnly);
    }

    return checkParamsCreativeOnly(currentRequestParams);
  }, [requestParams]);

  useEffect(() => {
    if (canShowPanel) {
      setIsLoading(true);
      reportsDatasource
        .getPageSpeed(currentRequestParams)
        .then(setPageSpeedData)
        .then(() => setIsLoading(false));
    }
  }, [currentRequestParams]);

  return (
    <PageSpeedContext.Provider
      value={{
        requestParamsIndex,
        downloadingReports,
        pageSpeedData,
        downloadPageSpeedReport,
        isLoading,
      }}
    >
      {canShowPanel ? children : <></>}
    </PageSpeedContext.Provider>
  );
};
